import { useState } from 'react'
import { Box, Collapse, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { STATUS_TYPES, LOCATION_ICONS, LINK_SPEEDS } from '../constants'
import { CaptionContainer, StatusCircle } from '../Styled'
import Switch from 'components/Switch'

const CaptionLine = ({ left, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24, gap: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
      {left}
    </Box>
    <Box sx={{ flex: 1, height: '1px', backgroundColor: '#F7F7F7', mx: 1 }} />
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      {right}
    </Box>
  </Box>
)

const ACTION_TABS = [
  { key: 'seal-question', icon: 'fa-regular fa-seal-question', titleKey: 'supervision.topology.caption.statuses' },
  { key: 'circle-nodes', icon: 'fa-regular fa-circle-nodes', titleKey: 'supervision.topology.caption.links' },
  { key: 'microchip', icon: 'fa-regular fa-microchip', titleKey: 'supervision.topology.caption.equipments' },
]

const TopologyCaption = ({ existingTypes = [], hiddenTypes = new Set(), onToggleType, defaultVisible = true, readonly = false, sx }) => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(defaultVisible)
  const [activeTab, setActiveTab] = useState('seal-question')

  const currentTab = ACTION_TABS.find(tab => tab.key === activeTab)

  const allVisible = hiddenTypes.size === 0
  const handleToggleAll = () => {
    if (readonly || !onToggleType) return
    if (allVisible) {
      existingTypes.forEach(eq => onToggleType(eq.value))
    } else {
      existingTypes.filter(eq => hiddenTypes.has(eq.value)).forEach(eq => onToggleType(eq.value))
    }
  }

  return (
    <CaptionContainer sx={{
      padding: visible ? '10px 0 8px' : '6px',
      minWidth: visible ? 200 : 'auto',
      width: visible ? 'auto' : 38,
      transition: 'padding 0.2s ease, min-width 0.2s ease, width 0.2s ease',
      ...sx
    }}>
      <Collapse in={!visible} timeout={180} unmountOnExit>
        <Box
          onClick={() => setVisible(true)}
          sx={{
            width: 24, height: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', borderRadius: '8px', color: '#464646',
            '&:hover': { backgroundColor: '#F3F4F6' },
          }}
        >
          <i className="fa-regular fa-subtitles" style={{ fontSize: 12 }} />
        </Box>
      </Collapse>

      <Collapse in={visible} timeout={220}>
        <Box>
          <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#989898' }}>{currentTab ? t(currentTab.titleKey) : ''}</Typography>
            {!readonly && activeTab === 'microchip' && existingTypes.length > 0 && (
              <Switch
                checked={allVisible}
                onChange={handleToggleAll}
                componentsProps={{ typography: { sx: { display: 'none' } } }}
                sx={{
                  m: 0,
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#9CA3AF',
                  },
                }}
              />
            )}
          </Box>

          <Box sx={{ px: 1.5, py: 0.5 }}>
            {activeTab === 'seal-question' && (
              <>
                {STATUS_TYPES.map(s => (
                  <CaptionLine
                    key={s.value}
                    left={<Typography sx={{ fontSize: 11, color: '#464646', fontWeight: 400 }}>{t(s.labelKey)}</Typography>}
                    right={<StatusCircle status={s.value} />}
                  />
                ))}
                <Box sx={{ height: 1, backgroundColor: '#EFEFEF', my: 0.75 }} />
                {LOCATION_ICONS.map(loc => (
                  <CaptionLine
                    key={loc.value}
                    left={<Typography sx={{ fontSize: 11, color: '#464646', fontWeight: 400 }}>{t(loc.labelKey)}</Typography>}
                    right={
                      loc.icon
                        ? <i className={`${loc.icon} fa-sm`} style={{ color: '#464646' }} />
                        : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Box sx={{ width: 5, height: '2px', backgroundColor: '#464646', borderRadius: 1 }} />
                            <Box sx={{ width: 5, height: '2px', backgroundColor: '#464646', borderRadius: 1 }} />
                            <Box sx={{ width: 5, height: '2px', backgroundColor: '#464646', borderRadius: 1 }} />
                          </Box>
                        )
                    }
                  />
                ))}
              </>
            )}
            {activeTab === 'circle-nodes' && LINK_SPEEDS.map(link => (
              <CaptionLine
                key={link.value}
                left={<Typography sx={{ fontSize: 11, color: '#464646', fontWeight: 400 }}>{t(link.labelKey)}</Typography>}
                right={<Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: link.color, flexShrink: 0 }} />}
              />
            ))}
            {activeTab === 'microchip' && existingTypes.map(eq => (
              <CaptionLine
                key={eq.value}
                left={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <i className={`${eq.icon} fa-sm`} style={{ color: '#464646', width: 14 }} />
                    <Typography sx={{ fontSize: 11, color: '#464646', fontWeight: 400 }}>{eq.labelKey ? t(eq.labelKey) : (eq.label || eq.value)}</Typography>
                  </Box>
                }
                right={!readonly ? (
                  <Switch
                    checked={!hiddenTypes.has(eq.value)}
                    onChange={() => onToggleType(eq.value)}
                    componentsProps={{ typography: { sx: { display: 'none' } } }}
                    sx={{ m: 0 }}
                  />
                ) : null}
              />
            ))}
          </Box>

          <Box sx={{ height: 1, backgroundColor: '#EFEFEF', my: 0.75 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
            <Box
              sx={{ flex: 1, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              onClick={() => setVisible(false)}
            >
              <Box sx={{
                width: 24, height: 24, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#464646', borderRadius: '8px',
                '&:hover': { backgroundColor: '#F3F4F6' },
              }}>
                <i className="fa-regular fa-xmark" style={{ fontSize: 11 }} />
              </Box>
            </Box>
            <Box sx={{ width: '1px', height: 24, backgroundColor: '#EFEFEF', mx: 0.5, flexShrink: 0 }} />
            {ACTION_TABS.map(tab => (
              <Box
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                sx={{ flex: 1, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <Box sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  color: activeTab === tab.key ? '#fff' : '#464646',
                  backgroundColor: activeTab === tab.key ? '#2570EB' : 'transparent',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                  ...(activeTab !== tab.key && { '&:hover': { backgroundColor: '#F3F4F6' } }),
                }}>
                  <i className={tab.icon} style={{ fontSize: 11 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </CaptionContainer>
  )
}

export default TopologyCaption

